/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: CSM.SYSTEM.PARAMETERS
 *
 * Generic key-value parameter store. Layout matches the rest of the admin
 * pages: search-filter grid + right-side edit drawer.
 *
 * Parameter values are typed via data_type_code (STRING / INT / BOOL / JSON)
 * and the editor renders the appropriate input. Validation runs client-side
 * before save.
 *
 * Notable parameter:
 *   EMAIL_OVERRIDE  - when non-empty, every notification email queued by the
 *                     server-side helpers gets redirected to this address with
 *                     "Sent to <original>" prepended to the body. See
 *                     csm_apply_email_override() for the actual rewrite.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { getForensicActorId } from '../../types/auth';
import { hasAccess, type AuthSession } from '../../types/auth';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { SortableHeader } from '../../components/common/SortableHeader';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { compareStrings, useSort } from '../../lib/use_sort';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, Plus, RefreshCw } from 'lucide-react';

type ParamSortKey = 'internal_code' | 'parameter_value' | 'data_type_code' | 'is_active' | 'description';

const PARAM_FIELD_DEFS: FieldDef[] = [
  { key: 'internal_code',   type: 'text',   label: 'Internal Code', required: true },
  { key: 'data_type_code',  type: 'select', label: 'Data Type' },
  { key: 'parameter_value', type: 'custom', label: 'Value',         col_span: 2 },
  { key: 'is_active',       type: 'boolean', label: 'Active',       col_span: 2 },
  { key: 'is_secret',       type: 'boolean', label: 'Secret',       col_span: 2 },
  { key: 'description',     type: 'textarea', label: 'Description',  col_span: 2 },
];
const PARAM_CORE_LAYOUT = toUldCoreLayout(PARAM_FIELD_DEFS);

// Core parameters that the runtime requires; UI refuses to delete these.
const PROTECTED_PARAMETERS = new Set([
  'EMAIL_OVERRIDE',
  'CSM.APP.ORIGIN',
  'CSM.FUNCTIONS.URL',
  'CSM.FUNCTIONS.TOKEN',
  'INTEGRYTAS.APP.NAME',
  'INTEGRYTAS.APP.BRANDING_SUFFIX',
  'CSM.SECURITY.OVERRIDE_PASSWORD_HASH',
  'CSM.AUDIT.EXCLUDED_TABLES',
]);

// The security-override password is stored ONLY as a bcrypt hash. It must never
// be edited as raw text through the generic value drawer (that would overwrite
// the hash with plaintext) - clicking its row opens the dedicated change modal,
// which routes through csm_set_override_password() to re-hash server-side.
const OVERRIDE_HASH_CODE = 'CSM.SECURITY.OVERRIDE_PASSWORD_HASH';
// The audit interceptor skip-list (JSON array of table names) maintained by the
// 20260609 migration; see csm_audit_is_excluded() / csm_enable_audit().
const AUDIT_EXCLUDED_CODE = 'CSM.AUDIT.EXCLUDED_TABLES';

type DataTypeCode = 'STRING' | 'INT' | 'BOOL' | 'JSON';

// Placeholder the UI shows for a secret parameter's value. Must match the
// gateway's SECRET_PARAM_MASK (supabase/functions/csm-api/index.ts) so a redacted
// payload and a locally-masked cell look identical. The raw value only ever
// arrives through the admin-gated csm_reveal_secret_parameter() rpc.
const SECRET_MASK = '••••••••';

interface ParamRow {
  system_parameter_id: number;
  internal_code: string;
  parameter_value: string;
  data_type_code: DataTypeCode;
  is_active: 'Y' | 'N';
  is_secret: 'Y' | 'N';
  description: string | null;
}

const FILTER_KEY = 'csm.sysparams.filter';

const GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code',   header: 'Code',        defaultWidth: 200 },
  { key: 'parameter_value', header: 'Value',        defaultWidth: 200 },
  { key: 'data_type_code',  header: 'Type',         defaultWidth: 80  },
  { key: 'is_active',       header: 'Active',       defaultWidth: 80  },
  { key: 'description',     header: 'Description',  defaultWidth: 280 },
];

export interface SystemParametersPageProps {
  session: AuthSession;
}

export function SystemParametersPage({ session }: SystemParametersPageProps) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <SystemParametersPageInner session={session} />
    </PersonalizationProvider>
  );
}


const PAGE_CODE = 'CSM.SYSTEM.PARAMETERS';
function SystemParametersPageInner({ session }: SystemParametersPageProps) {
  // §6/§17: the write below is page-scoped, so the affordance follows
  // csm_role_page_access. The server refuses it regardless; this stops the
  // page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const { t } = useTranslation();
  const { isPersonalizing } = usePersonalization();
  const [rows, setRows]       = useState<ParamRow[]>([]);
  const [filter, setFilter]   = useState(() => sessionStorage.getItem(FILTER_KEY) ?? '');
  const [editing, setEditing] = useState<ParamRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ParamRow | null>(null);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  // §17.B5: per-column multi-value facets (logical columns of the list).
  const [facets, setFacets] = useState<{ type: string[]; active: string[] }>({ type: [], active: ['Y'] });
  const sort = useSort<ParamSortKey>('csm.sysparams.sort', { key: 'internal_code', dir: 'asc' });
  // §17.B13: expand/contract related-children. System parameters are a flat
  // key-value store, so the control renders inert.
  const detail = useExpandedRows<number>();
  // TODO: wire relations — parameters are standalone; no child collection loaded.
  const buildRelatedGroups = (): RelatedChildGroup[] => [];

  useEffect(() => { sessionStorage.setItem(FILTER_KEY, filter); }, [filter]);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('csm_system_parameters')
        .select('system_parameter_id, internal_code, parameter_value, data_type_code, is_active, is_secret, description')
        .order('internal_code');
      if (e) throw e;
      setRows((data ?? []) as ParamRow[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  // Open a row for editing, except the override-password hash which routes to
  // its dedicated re-hashing modal instead of the raw-text value drawer.
  const openRow = (r: ParamRow) => {
    if (r.internal_code === OVERRIDE_HASH_CODE) { setChangingPw(true); return; }
    setEditing(r);
  };

  const handleDelete = async (r: ParamRow) => {
    setBusy(true); setError(null);
    try {
      const { error: dErr } = await apiDb
        .from('csm_system_parameters')
        .delete()
        .eq('system_parameter_id', r.system_parameter_id);
      if (dErr) throw dErr;
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      type:   uniq(rows.map(r => r.data_type_code)).map(v => ({ value: v, label: v })),
      active: [{ value: 'Y', label: t('common.boolean.active') }, { value: 'N', label: t('common.boolean.inactive') }],
    };
  }, [rows, t]);

  const facetsActive = facets.type.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ type: [], active: [] }); };

  const filtered = useMemo(() => {
    // A row passes a facet when no values are selected, or it matches one (OR
    // within a facet, AND across facets).
    const matchesFacets = (r: ParamRow) =>
      (facets.type.length   === 0 || facets.type.includes(r.data_type_code)) &&
      (facets.active.length === 0 || facets.active.includes(r.is_active));

    const base = (!filter.trim()
      ? rows
      : rows.filter(r => {
          const q = filter.toLowerCase();
          return r.internal_code.toLowerCase().includes(q) ||
            (r.parameter_value ?? '').toLowerCase().includes(q) ||
            (r.description ?? '').toLowerCase().includes(q);
        })).filter(matchesFacets);
    return sort.sorted(base, (a, b, key) => compareStrings((a as unknown as Record<string, unknown>)[key], (b as unknown as Record<string, unknown>)[key]));
  }, [rows, filter, facets, sort]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('system_parameters.list.title', 'System Parameters')}
          description={t('system_parameters.list.description', 'Key-value configuration parameters that control platform behaviour.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh')}
              </button>
              {canChange && (<button type="button" onClick={() => { setEditing(null); setCreating(false); setChangingPw(true); }} className="btn-secondary flex items-center gap-1.5">
                {t('system_parameters.list.change_override_password', 'Change Override Password')}
              </button>)}
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('system_parameters.list.new_parameter')}
              </button>)}
            </>
          }
        />

        <StatStrip
          stats={[
            { value: rows.length, label: t('system_parameters.stat.total', 'Total parameters') },
            { value: rows.filter(r => r.is_active === 'Y').length, label: t('system_parameters.stat.active', 'Active') },
          ]}
        />

        {error && (
          <ErrorBanner
            message={t('system_parameters.list.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text" value={filter} onChange={e => setFilter(e.target.value)}
                placeholder={t('system_parameters.list.search_placeholder')}
                className="h-8 w-80 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('system_parameters.list.columns.data_type', 'Type')}   options={facetOptions.type}   selected={facets.type}   onChange={v => setFacets(f => ({ ...f, type: v }))} />
            <FacetFilter label={t('system_parameters.list.columns.active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          {isPersonalizing && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
              {t('system_parameters.personalization_banner', 'Personalization mode — configure columns, then click the icon to save and exit.')}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-auto">
        {busy && rows.length === 0 ? <SkeletonRows rows={8} /> : (
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <SortableHeader<ParamSortKey> key={col.key}
                      sortKey={col.key as ParamSortKey} label={col.header}
                      currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort}
                      style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined} />
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('system_parameters.list.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.system_parameter_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.system_parameter_id)} hasChildren={false} onToggle={() => detail.toggle(r.system_parameter_id)} expandLabel={t('common.related.show', 'Show related records')} collapseLabel={t('common.related.hide', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{r.internal_code}</span>}
                        {col.key === 'parameter_value' && (
                          r.is_secret === 'Y'
                            ? <SecretValueCell row={r} />
                            : <span className="font-mono text-csm-sm">{renderValuePreview(r, t)}</span>
                        )}
                        {col.key === 'data_type_code' && r.data_type_code/* fk-display-ok: value-type enum (STRING/INT/BOOL/JSON), no lookup entity */}
                        {col.key === 'is_active' && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active')} noLabel={t('common.boolean.inactive')} />}
                        {col.key === 'description' && (
                          <span className="text-csm-ink-soft truncate" style={{ display: 'block', maxWidth: '24rem' }}>
                            {r.description || <span className="italic text-csm-watermark">{t('common.none')}</span>}
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                      <button onClick={(e) => { e.stopPropagation(); openRow(r); }}
                              className="mr-1 inline-block min-w-[3.25rem] rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('system_parameters.list.actions.edit')}</button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (PROTECTED_PARAMETERS.has(r.internal_code)) {
                            setError(t('system_parameters.list.errors.protected_param', { code: r.internal_code }));
                            return;
                          }
                          setDeleteTarget(r);
                        }}
                        disabled={PROTECTED_PARAMETERS.has(r.internal_code)}
                        title={PROTECTED_PARAMETERS.has(r.internal_code) ? t('system_parameters.list.actions.delete_disabled_tooltip') : t('system_parameters.list.actions.delete_tooltip')}
                        className="inline-block min-w-[3.75rem] rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:bg-csm-danger-soft disabled:cursor-not-allowed disabled:opacity-40"
                      >{t('system_parameters.list.actions.delete')}</button>
                    </td>
                  </tr>
                  {detail.isOpen(r.system_parameter_id) && (
                    <tr><td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRelatedGroups()} session={session} /></td></tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState
                      variant={filter ? 'no-match' : 'no-data'}
                      title={t('system_parameters.list.empty_title', 'No parameters found')}
                      description={filter ? t('system_parameters.list.empty_filtered', 'No parameters match "{{filter}}"', { filter }) : undefined}
                      action={!filter && canAdd ? (
                        <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">
                          + {t('system_parameters.list.new_parameter', 'New Parameter')}
                        </button>
                      ) : undefined}
                    />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <ParamEditModal
          session={session}
          row={editing}
          existingCodes={new Set(rows.map(r => r.internal_code))}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }}
        />
      )}

      {changingPw && (
        <OverridePasswordModal onClose={() => setChangingPw(false)} />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('system_parameters.list.delete_confirm_title', 'Delete Parameter?')}
          message={t('system_parameters.list.delete_confirm_message', 'Delete "{{code}}"? This cannot be undone.', { code: deleteTarget.internal_code })}
          onConfirm={async () => { await handleDelete(deleteTarget); setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function renderValuePreview(r: ParamRow, t: (key: string) => string): string {
  if (r.data_type_code === 'BOOL') return r.parameter_value === 'Y' || r.parameter_value === 'TRUE' ? t('common.yes') : t('common.no');
  if (!r.parameter_value) return t('system_parameters.list.value_empty');
  if (r.parameter_value.length > 60) return r.parameter_value.slice(0, 57) + '...';
  return r.parameter_value;
}

// Admin-gated reveal of a secret parameter's raw value. The gateway redacts the
// value on select, so this rpc (which re-checks csm_current_is_admin() server-side)
// is the only path to the plaintext. Returns the value or throws a translated error.
async function revealSecret(internalCode: string): Promise<string> {
  const { data, error } = await apiDb.rpc('csm_reveal_secret_parameter', { p_internal_code: internalCode });
  if (error) throw new Error(error.message);
  const res = data as { ok: boolean; value?: string; error_code?: string } | null;
  if (!res?.ok) throw new Error(res?.error_code ?? 'CSM.SECRET.REVEAL_FAILED');
  return res.value ?? '';
}

// Grid cell for a secret parameter: masked by default with an explicit,
// admin-only Reveal affordance (§12 — credentials never render in clear text
// without a deliberate action). Never renders the raw value on first paint.
function SecretValueCell({ row }: { row: ParamRow }) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onReveal = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true); setError(null);
    try {
      setRevealed(await revealSecret(row.internal_code));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const onHide = (e: React.MouseEvent) => { e.stopPropagation(); setRevealed(null); setError(null); };

  return (
    <span className="inline-flex items-center gap-2">
      <span className="font-mono text-csm-sm break-all">
        {revealed !== null
          ? (revealed || t('system_parameters.list.value_empty', '(empty)'))
          : SECRET_MASK}
      </span>
      {revealed === null ? (
        <button type="button" onClick={onReveal} disabled={busy}
          className="shrink-0 rounded border border-csm-border bg-white px-1.5 py-0.5 text-csm-xs hover:bg-csm-surface-alt disabled:opacity-50">
          {busy ? t('common.loading', 'Loading…') : t('system_parameters.list.reveal', 'Reveal')}
        </button>
      ) : (
        <button type="button" onClick={onHide}
          className="shrink-0 rounded border border-csm-border bg-white px-1.5 py-0.5 text-csm-xs hover:bg-csm-surface-alt">
          {t('system_parameters.list.hide', 'Hide')}
        </button>
      )}
      {error && <span className="text-csm-xs text-csm-danger">{t('system_parameters.list.reveal_failed', 'Reveal failed')}</span>}
    </span>
  );
}

// =============================================================================
// EDIT MODAL
// =============================================================================

interface ParamEditModalProps {
  session: AuthSession;
  row: ParamRow | null;
  existingCodes: Set<string>;
  onClose: () => void;
  onSaved: () => void;
}

function ParamEditModal({ session, row, existingCodes, onClose, onSaved }: ParamEditModalProps) {
  const { t } = useTranslation();
  const { isPersonalizing } = usePersonalization();
  const CK = 'drawer';
  const isCreate = row === null;
  // Editing an existing secret: the loaded value is the redacted mask, never the
  // plaintext. Start the field empty (blank means keep the current secret) so a
  // save can't overwrite the real secret with the mask; the admin reveals/edits
  // deliberately. Non-secret rows keep their normal prefilled value.
  const isExistingSecret = !!row && row.is_secret === 'Y';

  const initialValues = {
    internalCode: row?.internal_code ?? '',
    value: isExistingSecret ? '' : (row?.parameter_value ?? ''),
    dataType: (row?.data_type_code ?? 'STRING') as DataTypeCode,
    isActive: row ? row.is_active === 'Y' : true,
    isSecret: row ? row.is_secret === 'Y' : false,
    description: row?.description ?? '',
  };

  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  const { layout } = useUldLayout('CSM.SYSPARAMS.MODAL', PARAM_CORE_LAYOUT);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [busy, setBusy]   = useState(false);
  const [revealBusy, setRevealBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the current plaintext into the value field so an admin can edit from it.
  const revealIntoField = async () => {
    if (!row) return;
    setRevealBusy(true); setError(null);
    try {
      setForm({ ...form, value: await revealSecret(row.internal_code) });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRevealBusy(false);
    }
  };

  useEffect(() => { reset(initialValues); }, [row?.system_parameter_id]); // eslint-disable-line

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const validate = (): string | null => {
    if (isCreate && !form.internalCode.trim()) return t('system_parameters.modal.errors.code_required', 'Internal code is required.');
    if (isCreate && existingCodes.has(form.internalCode.trim().toUpperCase())) {
      return t('system_parameters.modal.errors.code_exists', 'A parameter with that code already exists.');
    }
    if (form.dataType === 'INT' && form.value !== '' && Number.isNaN(Number(form.value))) {
      return t('system_parameters.modal.errors.invalid_int', 'Value must be a valid integer.');
    }
    if (form.dataType === 'JSON' && form.value !== '') {
      try { JSON.parse(form.value); }
      catch { return t('system_parameters.modal.errors.invalid_json', 'Value must be valid JSON.'); }
    }
    return null;
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const v = validate();
      if (v) throw new Error(v);

      const actorId = getForensicActorId(session);
      const payload: Record<string, unknown> = {
        data_type_code: form.dataType,
        is_active: form.isActive ? 'Y' : 'N',
        is_secret: form.isSecret ? 'Y' : 'N',
        description: form.description || null,
        modified_by: actorId,
      };
      // A secret must never be world-readable via the csm_sp_select_public policy.
      if (form.isSecret) payload.is_public = 'N';

      // Value write policy: create always writes; a non-secret row always writes;
      // an EXISTING secret only writes when the admin actually typed a new value
      // (blank or the mask placeholder = keep the current secret untouched).
      const enteredMask = form.value === SECRET_MASK;
      const keepExistingSecret = isExistingSecret && (form.value === '' || enteredMask);
      if (!keepExistingSecret) payload.parameter_value = form.value;

      if (isCreate) {
        const { error: e } = await apiDb
          .from('csm_system_parameters')
          .insert({ internal_code: form.internalCode.trim().toUpperCase(), ...payload });
        if (e) throw e;
      } else {
        const { error: e } = await apiDb
          .from('csm_system_parameters')
          .update(payload)
          .eq('system_parameter_id', row?.system_parameter_id);
        if (e) throw e;
      }
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <Modal
      title={isCreate ? t('system_parameters.modal.create_title', 'Create Parameter') : t('system_parameters.modal.edit_title', 'Edit Parameter — {{code}}', { code: row?.internal_code ?? '' })}
      onClose={handleClose}
      width="xl"
      headerAction={<>{session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) && null}<PersonalizationToggle /></>}
      footer={
        <>
          <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy}
                  className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
            {busy ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          </button>
        </>
      }
    >
      {isPersonalizing && (
        <div className="sticky top-0 z-10 mb-3 rounded border border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
          {t('system_parameters.modal.personalization_banner', 'Toggle fields — hide or show. Click icon to save.')}
        </div>
      )}

      <div className="mb-3">
        <FieldRenderer
          fields={PARAM_FIELD_DEFS}
          layout={layout}
          columns={2}
          pageCode="CSM.SYSPARAMS.MODAL"
          session={session}
          render={(f) => {
            if (f.key === 'internal_code') return (
              <PersonalizedField componentKey={CK} fieldKey="internal_code" label={t('system_parameters.modal.fields.internal_code', 'Internal Code')}>
                <div>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('system_parameters.modal.fields.internal_code', 'Internal Code')}</label>
                  <input
                    type="text" value={form.internalCode} disabled={!isCreate}
                    onChange={e => setForm({ ...form, internalCode: e.target.value.toUpperCase() })}
                    onBlur={() => {
                      if (isCreate && form.internalCode && !/^[A-Z][A-Z0-9_.]*$/.test(form.internalCode)) {
                        setInternalCodeError(t('system_parameters.modal.errors.invalid_code', 'Must start with a letter — uppercase letters, numbers, dots only.'));
                      } else {
                        setInternalCodeError(null);
                      }
                    }}
                    placeholder="DOMAIN.PARAMETER.NAME"
                    className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted"
                  />
                  {internalCodeError
                    ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                    : <p className="text-csm-xs text-csm-ink-soft mt-1">{t('system_parameters.modal.code_hint', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>
                  }
                </div>
              </PersonalizedField>
            );
            if (f.key === 'data_type_code') return (
              <PersonalizedField componentKey={CK} fieldKey="data_type_code" label={t('system_parameters.modal.fields.data_type', 'Data Type')}>
                <div>
                  <FilteredModalSingleSelect
                    label={t('system_parameters.modal.fields.data_type', 'Data Type')}
                    options={[
                      { internal_code: 'STRING', display_text: 'STRING', description: t('system_parameters.modal.data_type.string_desc', 'Plain text value.') },
                      { internal_code: 'INT',    display_text: 'INT',    description: t('system_parameters.modal.data_type.int_desc', 'Whole number.') },
                      { internal_code: 'BOOL',   display_text: 'BOOL',   description: t('system_parameters.modal.data_type.bool_desc', 'Y / N boolean.') },
                      { internal_code: 'JSON',   display_text: 'JSON',   description: t('system_parameters.modal.data_type.json_desc', 'JSON object or array.') },
                    ]}
                    selectedCode={form.dataType}
                    onChange={(code) => { if (code) setForm({ ...form, dataType: code as DataTypeCode }); }}
                    allowClear={false}
                  />
                </div>
              </PersonalizedField>
            );
            if (f.key === 'parameter_value') return (
              <PersonalizedField componentKey={CK} fieldKey="parameter_value" label={t('system_parameters.modal.fields.value', 'Value')}>
                <div>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('system_parameters.modal.fields.value', 'Value')}</label>
                  {form.dataType === 'BOOL' ? (
                    <label className="flex h-8 items-center gap-2 text-csm-sm text-csm-ink-soft">
                      <input
                        type="checkbox"
                        checked={form.value === 'Y' || form.value.toUpperCase() === 'TRUE'}
                        onChange={e => setForm({ ...form, value: e.target.checked ? 'Y' : 'N' })}
                        className="h-4 w-4 accent-gray-700"
                      />
                      {form.value === 'Y' || form.value.toUpperCase() === 'TRUE' ? t('system_parameters.modal.bool_yes', 'Yes (Y)') : t('system_parameters.modal.bool_no', 'No (N)')}
                    </label>
                  ) : form.dataType === 'JSON' ? (
                    <textarea
                      rows={6} value={form.value} onChange={e => setForm({ ...form, value: e.target.value })}
                      className="w-full rounded border border-csm-border px-2 py-1 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none"
                    />
                  ) : form.dataType === 'INT' ? (
                    <input
                      type="number" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })}
                      className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
                    />
                  ) : (
                    <input
                      type="text" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })}
                      className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
                    />
                  )}
                  {isExistingSecret && (
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <button type="button" onClick={() => void revealIntoField()} disabled={revealBusy}
                        className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt disabled:opacity-50">
                        {revealBusy ? t('common.loading', 'Loading…') : t('system_parameters.modal.reveal_current', 'Reveal current value')}
                      </button>
                      <span className="text-csm-2xs text-csm-ink-soft">
                        {t('system_parameters.modal.secret_hint', 'Hidden — leave blank to keep the current secret, or type a new value to replace it.')}
                      </span>
                    </div>
                  )}
                  {row?.internal_code === 'EMAIL_OVERRIDE' && (
                    <div className="mt-1 text-csm-2xs text-csm-warning">
                      {t('system_parameters.modal.email_override_hint', 'Leave empty to disable. When non-empty, every notification email is sent to this address with "Sent to <original>" prepended to the body.')}
                    </div>
                  )}
                  {row?.internal_code === AUDIT_EXCLUDED_CODE && (
                    <div className="mt-1 text-csm-2xs text-csm-warning">
                      {t('system_parameters.modal.audit_excluded_hint_before', 'JSON array of table names excluded from automatic mutation auditing (e.g. high-volume log tables). Adding a table here stops new audit rows for it; removing one lets the interceptor re-attach. Run')}
                      <span className="font-mono"> select csm_enable_audit('table'); </span>
                      {t('system_parameters.modal.audit_excluded_hint_mid', 'to (re)attach immediately.')} <span className="font-mono">csm_audit_log</span>
                      {' '}{t('system_parameters.modal.audit_excluded_hint_after', 'is always excluded regardless of this list.')}
                    </div>
                  )}
                </div>
              </PersonalizedField>
            );
            if (f.key === 'is_active') return (
              <PersonalizedField componentKey={CK} fieldKey="is_active" label={t('system_parameters.modal.fields.active', 'Active')}>
                <div>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('system_parameters.modal.fields.active', 'Active')}</label>
                  <label className="flex h-8 items-center gap-2 text-csm-sm text-csm-ink-soft">
                    <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="h-4 w-4 accent-gray-700" />
                    {form.isActive ? t('common.yes', 'Yes') : t('common.no', 'No')}
                  </label>
                </div>
              </PersonalizedField>
            );
            if (f.key === 'is_secret') return (
              <PersonalizedField componentKey={CK} fieldKey="is_secret" label={t('system_parameters.modal.fields.secret', 'Secret')}>
                <div>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('system_parameters.modal.fields.secret', 'Secret')}</label>
                  <label className="flex h-8 items-center gap-2 text-csm-sm text-csm-ink-soft">
                    <input type="checkbox" checked={form.isSecret} onChange={e => setForm({ ...form, isSecret: e.target.checked })} className="h-4 w-4 accent-gray-700" />
                    {form.isSecret ? t('common.yes', 'Yes') : t('common.no', 'No')}
                  </label>
                  <p className="mt-1 text-csm-2xs text-csm-ink-soft">
                    {t('system_parameters.modal.secret_field_hint', 'Credential value: masked in the grid, redacted on read, revealed only on demand. Never marked public.')}
                  </p>
                </div>
              </PersonalizedField>
            );
            if (f.key === 'description') return (
              <PersonalizedField componentKey={CK} fieldKey="description" label={t('system_parameters.modal.fields.description', 'Description')}>
                <div>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('system_parameters.modal.fields.description', 'Description')}</label>
                  <textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                            className="w-full rounded border border-csm-border px-2 py-1 text-csm-sm focus:border-csm-watermark focus:outline-none" />
                </div>
              </PersonalizedField>
            );
            return null;
          }}
        />
      </div>

      {error && (
        <div className="mt-3">
          <ErrorBanner
            message={t('system_parameters.modal.save_failed', "Couldn't save the parameter.")}
            detail={error}
            details_label={t('common.details', 'Details')}
          />
        </div>
      )}
    </Modal>

    {showDiscard && (
      <ConfirmModal
        title={t('common.discard_changes_title', 'Discard changes?')}
        message={t('common.discard_changes_message', 'You have unsaved changes that will be lost.')}
        confirmLabel={t('common.discard', 'Discard')}
        cancelLabel={t('common.keep_editing', 'Keep editing')}
        danger={false}
        onConfirm={() => { reset(); onClose(); }}
        onCancel={() => setShowDiscard(false)}
      />
    )}
    </>
  );
}

// =============================================================================
// CHANGE OVERRIDE PASSWORD MODAL
//
// The global Security Override Password is stored only as a bcrypt-12 hash
// (csm_system_parameters -> CSM.SECURITY.OVERRIDE_PASSWORD_HASH). This modal
// never reads the hash; it submits a new plaintext to csm_set_override_password()
// which re-hashes server-side (admin-only, min 12 chars).
// =============================================================================

interface OverridePasswordModalProps {
  onClose: () => void;
}

const OVERRIDE_PW_ERROR_KEYS: Record<string, string> = {
  'CSM.OVERRIDE.INSUFFICIENT_PRIVILEGE': 'system_parameters.override_pw.errors.insufficient_privilege',
  'CSM.OVERRIDE.PASSWORD_TOO_SHORT':     'system_parameters.override_pw.errors.too_short',
};
const OVERRIDE_PW_ERROR_DEFAULTS: Record<string, string> = {
  'CSM.OVERRIDE.INSUFFICIENT_PRIVILEGE': 'You must be an administrator to change the override password.',
  'CSM.OVERRIDE.PASSWORD_TOO_SHORT':     'The override password must be at least 12 characters.',
};

function OverridePasswordModal({ onClose }: OverridePasswordModalProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone]   = useState(false);

  const submit = async () => {
    setError(null);
    if (password.length < 12) { setError(t('system_parameters.override_pw.too_short', 'The override password must be at least 12 characters.')); return; }
    if (password !== confirm) { setError(t('system_parameters.override_pw.mismatch', 'The two passwords do not match.')); return; }

    setBusy(true);
    try {
      const { data, error: rpcError } = await apiDb.rpc('csm_set_override_password', {
        p_new_password: password,
      });
      if (rpcError) throw new Error(rpcError.message);
      const res = data as { ok: boolean; error_code?: string } | null;
      if (!res?.ok) {
        const code = res?.error_code ?? '';
        const mappedKey = OVERRIDE_PW_ERROR_KEYS[code];
        throw new Error(
          mappedKey
            ? t(mappedKey, OVERRIDE_PW_ERROR_DEFAULTS[code])
            : (code || t('system_parameters.override_pw.errors.generic', 'Failed to change the override password.')),
        );
      }
      setDone(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t('system_parameters.override_pw.title', 'Change Override Password')}
      onClose={onClose}
      width="lg"
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">
            {done ? t('common.close', 'Close') : t('common.cancel', 'Cancel')}
          </button>
          {!done && (
            <button onClick={() => void submit()} disabled={busy}
                    className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
              {busy ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
            </button>
          )}
        </>
      }
    >
      {done ? (
        <div className="rounded border border-csm-border bg-csm-surface-alt px-3 py-2 text-csm-sm text-csm-ink-soft">
          {t('system_parameters.override_pw.updated', 'The security override password has been updated.')}
        </div>
      ) : (
        <>
          <p className="mb-3 text-csm-2xs text-csm-ink-soft">
            {t('system_parameters.override_pw.intro', 'Used to authorize restricted actions. Stored only as a bcrypt hash — never recoverable. Minimum 12 characters.')}
          </p>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('system_parameters.override_pw.new_password', 'New Password')}</label>
          <input
            type="password" value={password} autoComplete="new-password"
            onChange={e => setPassword(e.target.value)}
            className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
          />
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('system_parameters.override_pw.confirm_password', 'Confirm Password')}</label>
          <input
            type="password" value={confirm} autoComplete="new-password"
            onChange={e => setConfirm(e.target.value)}
            className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
          />
          {error && (
            <div className="mt-3">
              <ErrorBanner
                message={t('system_parameters.override_pw.save_failed', "Couldn't change the override password.")}
                detail={error}
                details_label={t('common.details', 'Details')}
              />
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
